import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { setupCopy } from '../../copy/setup';

// Small pieces shared by the setup card, the drawers and the Setup pages.
// Kept apart from SetupDrawer so the drawers can load only when opened.

/** The window event that makes SetupProvider refetch (a SignalR nudge, a save that changes setup). */
export const SETUP_STATUS_EVENT = 'cadence:setup-status-changed';

/** Ask SetupProvider to refetch now (after something here changed the person's setup). */
export function announceSetupChanged(): void {
  window.dispatchEvent(new CustomEvent(SETUP_STATUS_EVENT));
}

export function fill(template: string, values: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in values ? String(values[k]) : m));
}

/** An ISO time as "HH:mm" in the person's zone. */
export function clock(iso: string | null | undefined, timeZone: string): string {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone }).format(new Date(iso));
  } catch {
    return new Date(iso).toTimeString().slice(0, 5);
  }
}

/**
 * Copies `text`. If the browser won't (no clipboard, permission denied), the
 * text is shown instead so it can still be selected by hand.
 */
export function CopyButton({ text, label = setupCopy.drawer.copy, ariaLabel, onCopy, primary }: {
  text: string; label?: string; ariaLabel?: string; onCopy?: () => void; primary?: boolean;
}) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  return (
    <span className="inline-flex flex-col gap-2">
      <Button
        type="button"
        size="sm"
        variant={primary ? 'default' : 'outline'}
        className="h-8 shrink-0 gap-1.5 self-start"
        aria-label={ariaLabel}
        onClick={async () => {
          try {
            if (!navigator.clipboard) throw new Error('no clipboard');
            await navigator.clipboard.writeText(text);
            setState('copied');
            window.setTimeout(() => setState('idle'), 1800);
          } catch {
            setState('failed');
          }
          onCopy?.();
        }}
      >
        {state === 'copied' ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
        {state === 'copied' ? setupCopy.drawer.copied : label}
      </Button>
      {state === 'failed' && (
        <span className="block max-w-full">
          <span className="block text-[12px] text-muted-foreground">{setupCopy.drawer.copyFailed}</span>
          <textarea readOnly value={text} rows={Math.min(8, text.split('\n').length + 1)} aria-label={ariaLabel ?? label}
            className="mt-1 w-full rounded-md border border-border bg-background p-2 font-plex text-[12px]"
            onFocus={(e) => e.currentTarget.select()} />
        </span>
      )}
    </span>
  );
}

export function CopyField({ label, value, copyLabel, onCopy, multiline }: {
  label?: string; value: string; copyLabel?: string; onCopy?: () => void; multiline?: boolean;
}) {
  return (
    <div className={cn('mt-2 flex gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2', multiline ? 'items-start' : 'items-center')}>
      {label && <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>}
      <code className={cn('min-w-0 flex-1 font-plex text-[12.5px] text-foreground', multiline ? 'whitespace-pre-wrap' : 'truncate')}>{value}</code>
      <CopyButton text={value} onCopy={onCopy} ariaLabel={copyLabel ?? (label ? `${setupCopy.drawer.copy} ${label}` : undefined)} />
    </div>
  );
}

/** A status pill, readable (4.5:1) in light and dark. */
export function Pill({ tone, children }: { tone: 'ok' | 'next' | 'bad' | 'warn' | 'muted'; children: React.ReactNode }) {
  return (
    <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
      tone === 'ok' && 'bg-emerald-500/15 text-emerald-800 dark:text-emerald-300',
      tone === 'next' && 'bg-primary/15 text-blue-800 dark:text-primary',
      tone === 'bad' && 'bg-red-500/15 text-red-800 dark:text-red-300',
      tone === 'warn' && 'bg-amber-500/15 text-amber-900 dark:text-amber-300',
      tone === 'muted' && 'bg-muted text-muted-foreground')}>
      {children}
    </span>
  );
}

function localDay(d: Date, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone }).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** A local calendar date as a day count, so differences are whole days whatever the clocks did. */
function dayNumber(d: Date, timeZone: string): number {
  const [y, m, day] = localDay(d, timeZone).split('-').map(Number);
  return Date.UTC(y, m - 1, day) / 86_400_000;
}

/**
 * "today at 11:00", "yesterday at 17:30", "tomorrow at 08:30", "Monday at
 * 11:00" within a week, else "14 September at 11:00", in the person's zone.
 */
export function whenIn(iso: string | null | undefined, timeZone: string, now: Date = new Date()): string {
  if (!iso) return '';
  const at = new Date(iso);
  const time = clock(iso, timeZone);
  const diff = dayNumber(at, timeZone) - dayNumber(now, timeZone);
  if (diff === 0) return fill(setupCopy.health.today, { time });
  if (diff === -1) return fill(setupCopy.health.yesterday, { time });
  if (diff === 1) return fill(setupCopy.health.tomorrow, { time });
  const format = (options: Intl.DateTimeFormatOptions) => {
    try {
      return new Intl.DateTimeFormat('en-GB', { ...options, timeZone }).format(at);
    } catch {
      return at.toLocaleDateString('en-GB', options);
    }
  };
  return Math.abs(diff) <= 6
    ? fill(setupCopy.health.onDay, { day: format({ weekday: 'long' }), time })
    : fill(setupCopy.health.onDay, { day: format({ day: 'numeric', month: 'long' }), time });
}

/** A setup page section: the small uppercase title, then rows. */
export function SetupSection({ title, children, footer }: { title: React.ReactNode; children: React.ReactNode; footer?: React.ReactNode }) {
  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm">
      {/* Under Setup health's h3 in Settings → Assistants & agents. */}
      <h4 className="px-5 pb-1.5 pt-4 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{title}</h4>
      <div className="flex flex-col">{children}</div>
      {footer && <div className="flex flex-wrap gap-2.5 border-t border-border px-5 py-3.5">{footer}</div>}
    </section>
  );
}

/** One row: icon tile, title and summary, actions on the right. */
export function SetupRow({ icon, title, sub, tone, actions }: {
  icon: React.ReactNode; title: React.ReactNode; sub?: React.ReactNode; tone?: 'warn'; actions?: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[32px_minmax(0,1fr)] items-center gap-x-3.5 gap-y-2 border-t border-border px-5 py-3.5 first:border-t-0 sm:grid-cols-[32px_minmax(0,1fr)_auto]">
      <span className="grid h-8 w-8 place-items-center rounded-lg bg-accent text-foreground" aria-hidden>{icon}</span>
      <div className="min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        {sub && <p className={cn('text-[12.5px]', tone === 'warn' ? 'text-amber-800 dark:text-amber-300' : 'text-muted-foreground')}>{sub}</p>}
      </div>
      {actions && <div className="col-start-2 flex flex-wrap items-center gap-2.5 sm:col-start-auto sm:justify-end">{actions}</div>}
    </div>
  );
}

